import { recognizeOnDevice } from "./nativeRecognition";
import { apiBase } from "./api";
import { getDeviceId } from "./storage";

export interface VoiceConfig {
  transcriptionAvailable: boolean;
  synthesisAvailable: boolean;
  transcriptionProvider: string;
  synthesisProvider: string;
  maxRecordingSeconds: number;
}
export async function voiceConfig(): Promise<VoiceConfig> {
  const native = typeof window !== "undefined" && !!window.DerechRecognition;
  try {
    const response = await fetch(`${apiBase}/api/voice/config`, { cache: "no-store" });
    if (!response.ok) throw new Error("שירות הדיבור אינו זמין");
    const config: VoiceConfig = await response.json();
    return { ...config, transcriptionAvailable: native || config.transcriptionAvailable,
      synthesisAvailable: (typeof window !== "undefined" && !!window.DerechSpeech) || config.synthesisAvailable,
      transcriptionProvider: native ? "device" : config.transcriptionProvider };
  } catch (error) {
    if (!native) throw error;
    return { transcriptionAvailable: true, synthesisAvailable: (typeof window !== "undefined" && !!window.DerechSpeech),
      transcriptionProvider: "device", synthesisProvider: "device", maxRecordingSeconds: 20 };
  }
}

export async function transcribeAudio(
  audio: Blob,
  signal: AbortSignal,
): Promise<string> {
  if (typeof window !== "undefined" && window.DerechRecognition) {
    try {
      const result = await recognizeOnDevice(audio, signal);
      signal.throwIfAborted();
      // Empty is a successful recognition attempt, never upload silence as fallback.
      return result.trim();
    } catch (error) {
      signal.throwIfAborted();
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      window.dispatchEvent(new CustomEvent("desty:voice-provider", { detail: "זיהוי המכשיר אינו זמין · משתמשים בשירות הגיבוי שלך" }));
    }
  }
  const extension = audio.type.includes("mp4")
    ? "mp4"
    : audio.type.includes("ogg")
      ? "ogg"
      : audio.type.includes("wav")
        ? "wav"
        : "webm";
  const form = new FormData();
  form.append("file", audio, `recording.${extension}`);
  const response = await fetch(`${apiBase}/api/voice/transcribe`, {
    method: "POST",
    body: form,
    signal,
    cache: "no-store",
    headers: {
      "X-Device-Id": getDeviceId(),
    },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error("שירות הדיבור אינו זמין");
  if (typeof body?.text !== "string" || !body.text.trim())
    throw new Error("לא זוהה דיבור ברור");
  return body.text.trim();
}

export type SpeechPlaybackEvent =
  | { state: "preparing" | "playing" | "ended" | "cancelled" }
  | { state: "error"; message: string };

interface AudioRequest {
  text: string;
  promise: Promise<ArrayBuffer>;
  resolve: (bytes: ArrayBuffer) => void;
  reject: (error: Error) => void;
  isNeeded: () => boolean;
  controller?: AbortController;
  timeout?: ReturnType<typeof setTimeout>;
  settled: boolean;
}

const normalizedSpeech = (text: string) => text.trim().slice(0, 180);
const unavailableMessage = "שירות הדיבור אינו זמין";

export class F5Player {
  private context: AudioContext | null = null;
  private source: AudioBufferSourceNode | null = null;
  private generation = 0;
  private cache = new Map<string, ArrayBuffer>();
  private requests = new Map<string, AudioRequest>();
  private queue: AudioRequest[] = [];
  private active: AudioRequest | null = null;
  unlock() {
    try {
      if (!this.context || this.context.state === "closed") {
        const AudioContextClass = globalThis.AudioContext ??
          (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextClass) return;
        this.context = new AudioContextClass();
      }
      void this.context.resume().catch(() => {});
    } catch {}
  }
  stop(preserveText?: string) {
    this.generation++;
    try {
      this.source?.stop();
    } catch {}
    try {
      this.source?.disconnect();
    } catch {}
    this.source = null;
    const retained = preserveText === undefined ? undefined : normalizedSpeech(preserveText);
    for (const request of this.requests.values()) {
      if (request.text !== retained) this.cancelRequest(request);
    }
  }
  private cancelRequest(request: AudioRequest) {
    if (request.settled) return;
    request.settled = true;
    clearTimeout(request.timeout);
    request.controller?.abort();
    request.reject(new DOMException("Obsolete instruction", "AbortError"));
    this.requests.delete(request.text);
    this.queue = this.queue.filter((queued) => queued !== request);
    if (this.active === request) this.active = null;
  }
  private load(text: string, isNeeded: () => boolean = () => true, foreground = false) {
    text = normalizedSpeech(text);
    const cached = this.cache.get(text);
    if (cached) return Promise.resolve(cached);
    let request = this.requests.get(text);
    if (!request) {
      let resolve!: AudioRequest["resolve"], reject!: AudioRequest["reject"];
      const promise = new Promise<ArrayBuffer>((yes, no) => { resolve = yes; reject = no; });
      // Cancellation can precede the consumer awaiting a speculative request.
      void promise.catch(() => {});
      request = { text, promise, resolve, reject, isNeeded, settled: false };
      this.requests.set(text, request);
      this.queue.push(request);
    }
    if (foreground) {
      request.isNeeded = isNeeded;
      if (this.active !== request) {
        this.queue = [request, ...this.queue.filter((queued) => queued !== request)];
      }
    }
    this.startNext();
    return request.promise;
  }
  private startNext() {
    if (this.active) return;
    const request = this.queue.shift();
    if (!request) return;
    if (!request.isNeeded()) {
      this.cancelRequest(request);
      this.startNext();
      return;
    }
    this.active = request;
    request.controller = new AbortController();
    request.timeout = setTimeout(() => {
      this.cancelRequest(request);
      this.startNext();
    }, 60_000);
    void this.fetchAudio(request).then(
      (bytes) => {
        if (request.settled) return;
        this.cache.set(request.text, bytes);
        if (this.cache.size > 36) this.cache.delete(this.cache.keys().next().value!);
        this.finishRequest(request);
        request.resolve(bytes);
      },
      (error: Error) => {
        if (request.settled) return;
        this.finishRequest(request);
        request.reject(error);
      },
    );
  }
  private finishRequest(request: AudioRequest) {
    clearTimeout(request.timeout);
    request.settled = true;
    if (this.requests.get(request.text) === request) this.requests.delete(request.text);
    if (this.active === request) this.active = null;
    this.startNext();
  }
  private async fetchAudio(request: AudioRequest) {
    const response = await fetch(`${apiBase}/api/voice/speak`, {
      method: "POST",
      cache: "no-store",
      signal: request.controller!.signal,
      headers: { "Content-Type": "application/json", "X-Device-Id": getDeviceId() },
      body: JSON.stringify({ text: request.text }),
    });
    if (!response.ok || !response.headers.get("Content-Type")?.toLowerCase().startsWith("audio/"))
      throw new Error(unavailableMessage);
    return response.arrayBuffer();
  }
  prefetch(texts: string[]) {
    for (const text of [...new Set(texts.map(normalizedSpeech))].slice(0, 2)) {
      if (this.requests.size >= 2) return;
      if (text) void this.load(text).catch(() => {});
    }
  }
  async speak(
    text: string,
    isCurrent: () => boolean,
    onStatus: (event: SpeechPlaybackEvent) => void,
  ) {
    text = normalizedSpeech(text);
    // Keep the matching preload, but abandon other work before current guidance.
    this.stop(text);
    const generation = this.generation;
    if (!text || !isCurrent()) { onStatus({ state: "cancelled" }); return; }
    onStatus({ state: "preparing" });
    try {
      const bytes = await this.load(
        text,
        () => generation === this.generation && isCurrent(),
        true,
      );
      if (generation !== this.generation) return;
      if (!isCurrent()) {
        onStatus({ state: "cancelled" });
        return;
      }
      this.unlock();
      if (!this.context) throw new Error("המכשיר לא מאפשר השמעת קול");
      // resume() is asynchronous, especially when returning from an OS dialog.
      await this.context.resume();
      if (this.context.state !== "running")
        throw new Error("לחצו על כפתור הקול כדי לאפשר השמעה");
      let buffer: AudioBuffer;
      try {
        buffer = await this.context.decodeAudioData(bytes.slice(0));
      } catch (error) {
        this.cache.delete(text);
        throw error;
      }
      if (generation !== this.generation) return;
      if (!isCurrent()) {
        onStatus({ state: "cancelled" });
        return;
      }
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.context.destination);
      this.source = source;
      source.onended = () => {
        try { source.disconnect(); } catch {}
        if (this.source === source) this.source = null;
        if (generation === this.generation) onStatus({ state: "ended" });
      };
      source.start();
      onStatus({ state: "playing" });
    } catch {
      if (generation === this.generation) {
        try { this.source?.disconnect(); } catch {}
        this.source = null;
        onStatus(isCurrent() ? { state: "error", message: unavailableMessage } : { state: "cancelled" });
      }
    }
  }
}
export const f5Speech = new F5Player();
